import { Activity, useEffect, useState } from 'react'
import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'

import fetchWithAuth from '../../utilities/fetchWithAuth'
import { usePageTitle } from '../../context/PageTitleContext'

const EditUserPage = () => {
  const { uuid } = useParams()
  const navigate = useNavigate()
  const { setBreadcrumbs } = usePageTitle()

  const [userData, setUserData] = useState({
    email: '',
    profile: { firstName: '', lastName: '' },
    organization: '',
    role: '',
  })
  const [organizations, setOrganizations] = useState([])
  const [roles, setRoles] = useState([])
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userResp, orgResp, rolesResp] = await Promise.all([
          fetchWithAuth(`users/${uuid}`),
          fetchWithAuth('organizations'),
          fetchWithAuth('roles'),
        ])

        const user = userResp.data
        setUserData({
          email: user.email || '',
          profile: {
            firstName: user.profile?.firstName || '',
            lastName: user.profile?.lastName || '',
          },
          organization: user.organization?.uuid || '',
          role: user.role || '',
        })
        setBreadcrumbs([
          'users',
          `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim(),
          'edit',
        ])

        setOrganizations(orgResp.data.data || [])
        setRoles(rolesResp.data.data || [])
      } catch (error) {
        console.error('Error fetching user data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
    return () => setBreadcrumbs([])
  }, [uuid])

  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? ''
          : 'Please enter a valid email address'
      case 'firstName':
      case 'lastName':
        return value.length >= 2 && value.length <= 35
          ? ''
          : 'Must be between 2 and 35 characters'
      case 'organization':
        return value ? '' : 'Organization must be selected'
      case 'role':
        return value ? '' : 'Role must be selected'
      default:
        return ''
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    let updated
    if (name === 'firstName' || name === 'lastName') {
      updated = { ...userData, profile: { ...userData.profile, [name]: value } }
    } else {
      updated = { ...userData, [name]: value }
    }

    setUserData(updated)
    const error = validateField(name, value)
    setErrors((prev) => ({ ...prev, [name]: error }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const newErrors = {}

    newErrors.email = validateField('email', userData.email)
    newErrors.firstName = validateField('firstName', userData.profile.firstName)
    newErrors.lastName = validateField('lastName', userData.profile.lastName)
    newErrors.organization = validateField(
      'organization',
      userData.organization,
    )
    newErrors.role = validateField('role', userData.role)

    setErrors(newErrors)

    if (Object.values(newErrors).every((err) => err === '')) {
      try {
        const payload = {
          email: userData.email,
          profile: userData.profile,
          organization: userData.organization,
          role: userData.role,
        }

        const { status } = await fetchWithAuth(`users/${uuid}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })

        if (status === 200) {
          navigate(`/users/${uuid}`)
        } else {
          console.error('Error updating user:', status)
        }
      } catch (error) {
        console.error('Error updating user:', error)
      }
    }
  }

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mx: 'auto', maxWidth: '600px' }}>
        <Typography>Loading...</Typography>
      </Paper>
    )
  }

  return (
    <Paper sx={{ p: 3, mx: 'auto', maxWidth: '600px' }}>
      <Typography variant="h6" component="h1" gutterBottom>
        Edit User
      </Typography>
      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            label="Email"
            name="email"
            type="email"
            value={userData.email}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.email}
            helperText={errors.email}
          />

          <TextField
            label="First Name"
            name="firstName"
            value={userData.profile.firstName}
            onChange={handleChange}
            fullWidth
            error={!!errors.firstName}
            helperText={errors.firstName}
          />
          <TextField
            label="Last Name"
            name="lastName"
            value={userData.profile.lastName}
            onChange={handleChange}
            fullWidth
            error={!!errors.lastName}
            helperText={errors.lastName}
          />

          <FormControl fullWidth error={!!errors.organization}>
            <InputLabel id="organization-label" htmlFor="organization">
              Organization
            </InputLabel>
            <Select
              labelId="organization-label"
              name="organization"
              value={userData.organization}
              label="Organization"
              inputProps={{ id: 'organization' }}
              onChange={handleChange}
              required
            >
              {organizations.map((org) => (
                <MenuItem key={org.uuid} value={org.uuid}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
            <Activity mode={errors.organization ? 'visible' : 'hidden'}>
              <FormHelperText>{errors.organization}</FormHelperText>
            </Activity>
          </FormControl>

          <FormControl fullWidth error={!!errors.role}>
            <InputLabel id="role-label" htmlFor="role">
              Role
            </InputLabel>
            <Select
              labelId="role-label"
              name="role"
              value={userData.role}
              label="Role"
              inputProps={{ id: 'role' }}
              onChange={handleChange}
              required
            >
              {roles &&
                roles.map((r) => (
                  <MenuItem key={r.name} value={r.name}>
                    {r.name
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                  </MenuItem>
                ))}
            </Select>
            <Activity mode={errors.role ? 'visible' : 'hidden'}>
              <FormHelperText>{errors.role}</FormHelperText>
            </Activity>
          </FormControl>

          <Button variant="contained" color="primary" type="submit">
            Save Changes
          </Button>
        </Stack>
      </form>
    </Paper>
  )
}

export default EditUserPage
